import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  BookOpen,
  Hammer,
  Brain,
  CheckCircle2,
  CloudRain,
  GitBranch,
  Heart,
  History,
  Lightbulb,
  Loader2,
  PauseCircle,
  Plus,
  Smile,
  Target,
  UserRound,
} from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import useRetryFetch from "../../hooks/useRetryFetch";
import {
  MmEvent,
  MmMenu,
  MmObject,
  MmObjectType,
  MmProfile,
  MmVisit,
  aiTargetState,
  applyOperator,
  changeStatus,
  closeVisit,
  createObject,
  getBiography,
  inferProfile,
  listObjects,
  openVisit,
  snapshotProfile,
  writebackConfirm,
  writebackPreview,
} from "../../api/mentalModels";
import { getStoredRegister, storeRegister, useMmText } from "../../hooks/useMmText";
import type { MmRegister } from "../../api/mentalModels";
import MmProfilePanel from "./MmProfilePanel";
import MmOperatorFlow from "./MmOperatorFlow";
import MmBiography from "./MmBiography";
import MmPathPicker from "./MmPathPicker";
import MmPathEditor from "./MmPathEditor";
import { MmPath, createTemplate, getMastery, type MmVisitReason, type MmOperatorMastery } from "../../api/mentalModels";
import MmMasteryPanel from "./MmMasteryPanel";
import MmRealityShaping from "./MmRealityShaping";

/**
 * Standalone surface of the Mental Model Engine (design §10.5) — hosted as the
 * "Mental Models" tab in My Workspace. Flow: list → capture/profile → visit.
 */

const OBJECT_TYPES: MmObjectType[] = [
  "belief", "fear", "goal", "decision", "self_image", "value", "memory", "other",
];

/** Calm type visuals: icon + tint for list medallions and type chips. */
const TYPE_VISUALS: Record<string, { icon: React.ElementType; bg: string; fg: string }> = {
  belief: { icon: Lightbulb, bg: "bg-sky-50", fg: "text-sky-600" },
  fear: { icon: CloudRain, bg: "bg-slate-100", fg: "text-slate-500" },
  goal: { icon: Target, bg: "bg-emerald-50", fg: "text-emerald-600" },
  decision: { icon: GitBranch, bg: "bg-amber-50", fg: "text-amber-600" },
  self_image: { icon: UserRound, bg: "bg-violet-50", fg: "text-violet-600" },
  value: { icon: Heart, bg: "bg-rose-50", fg: "text-rose-500" },
  memory: { icon: BookOpen, bg: "bg-orange-50", fg: "text-orange-500" },
  attitude: { icon: Smile, bg: "bg-teal-50", fg: "text-teal-600" },
  other: { icon: Brain, bg: "bg-muted", fg: "text-muted-foreground" },
};

const typeVisual = (type: string) => TYPE_VISUALS[type] || TYPE_VISUALS.other;

type View =
  | { name: "list" }
  | { name: "capture" }
  | {
      name: "session";
      object: MmObject;
      visit: MmVisit;
      menu: MmMenu;
      warnings: string[];
      /** ICDF §2 — set when the visit was opened with a reason */
      reasonStats?: { frictionVisits: number };
      reasonKind?: "friction" | "plan";
    }
  | { name: "biography"; object: MmObject };

interface Props {
  onCountChange?: (count: number) => void;
}

const MmStudio: React.FC<Props> = ({ onCountChange }) => {
  const { t, i18n } = useTranslation();
  const retryFetch = useRetryFetch();
  const [register, setRegister] = useState<MmRegister>(getStoredRegister());
  const { tm } = useMmText(register);

  const [view, setView] = useState<View>({ name: "list" });
  const [objects, setObjects] = useState<MmObject[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // ICDF §4 — operator mastery (derived server-side from mm_events)
  const [mastery, setMastery] = useState<MmOperatorMastery[] | null>(null);
  const [reasonMix, setReasonMix] = useState<{ friction: number; plan: number }>({
    friction: 0,
    plan: 0,
  });
  useEffect(() => {
    getMastery(retryFetch)
      .then((res) => {
        setMastery(res.operators);
        setReasonMix(res.reasonMix);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const newOperatorIds = (mastery ?? [])
    .filter((m) => m.level === "new")
    .map((m) => m.operatorId);

  // Capture state
  const [statement, setStatement] = useState("");
  const [objectType, setObjectType] = useState<MmObjectType>("other");
  const [aiProfile, setAiProfile] = useState<MmProfile | null>(null);
  const [aiConfidence, setAiConfidence] = useState<Record<string, number>>({});
  const [profileDraft, setProfileDraft] = useState<MmProfile>({});
  const [distressPaused, setDistressPaused] = useState(false);
  const [pendingObject, setPendingObject] = useState<MmObject | null>(null);

  // Goal-directed mode (capture step 2)
  const [statedOutcome, setStatedOutcome] = useState("");
  // In-session goal editing (re-set the goal, get a new plan)
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalDraft, setGoalDraft] = useState("");
  // Predefined path selection (capture step + in-session)
  const [chosenPath, setChosenPath] = useState<MmPath | null>(null);
  const [pickingPath, setPickingPath] = useState(false);
  // Save/build a reusable path (opens the path editor)
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [buildingPath, setBuildingPath] = useState(false);
  // Bumped after template changes so path pickers remount with fresh data
  const [pathsVersion, setPathsVersion] = useState(0);
  // Write-back proposal shown when ending a session on a lifted object
  const [writeback, setWriteback] = useState<{
    objectId: string;
    current: string;
    proposed: string;
  } | null>(null);
  // Operator requested via a stepper chip click (consumed by MmOperatorFlow)
  const [requestedOperator, setRequestedOperator] = useState<string | null>(null);

  // Session state
  const [delta, setDelta] = useState<{ before: MmProfile; after: MmProfile } | null>(null);
  const [emittedNotes, setEmittedNotes] = useState<string[]>([]);
  // Post-operator re-rating (design §4: operate → re-profile)
  const [rerate, setRerate] = useState<{ before: MmProfile; draft: MmProfile } | null>(null);

  // Biography state
  const [bioEvents, setBioEvents] = useState<MmEvent[]>([]);
  const [bioSeries, setBioSeries] = useState<Record<string, Array<{ t: string; v: number }>>>({});
  // When History is opened from inside a session, stash it so Back returns
  // to the SAME session (same visit) instead of the list
  const [bioReturn, setBioReturn] = useState<View | null>(null);

  const language = i18n.language?.split("-")[0] || "en";

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listObjects(retryFetch, { sort: "attention" });
      setObjects(res.objects);
      onCountChange?.(res.objects.filter((o) => o.status === "active").length);
    } catch {
      /* list stays as-is */
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refresh();
    // Lift buttons store the object to open before switching to this tab
    const pendingId = sessionStorage.getItem("mm_open_object");
    if (pendingId) {
      sessionStorage.removeItem("mm_open_object");
      getBiography(retryFetch, pendingId)
        .then((res) => requestOpen(res.object))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const changeRegister = (r: MmRegister) => {
    setRegister(r);
    storeRegister(r);
  };

  // ---- capture flow ---------------------------------------------------------

  const startCapture = () => {
    setStatement("");
    setStatedOutcome("");
    setChosenPath(null);
    setBuildingPath(false);
    setObjectType("other");
    setAiProfile(null);
    setAiConfidence({});
    setProfileDraft({});
    setDistressPaused(false);
    setPendingObject(null);
    setView({ name: "capture" });
  };

  const captureAndInfer = async () => {
    if (statement.trim().length < 3) return;
    setBusy(true);
    try {
      const created = await createObject(retryFetch, { statement: statement.trim(), objectType });
      setPendingObject(created.object);
      setProfileDraft(created.object.profile);
      try {
        const inferred = await inferProfile(retryFetch, {
          statement: statement.trim(),
          objectType,
          language,
        });
        if (inferred.distress) {
          setDistressPaused(true);
          return;
        }
        if (inferred.profile) {
          setAiProfile(inferred.profile);
          setAiConfidence(inferred.confidence || {});
          setProfileDraft({ ...created.object.profile, ...inferred.profile });
        }
      } catch {
        /* inference is optional — user rates manually */
      }
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const beginVisit = async () => {
    if (!pendingObject) return;
    setBusy(true);
    try {
      // Persist the (possibly corrected) profile before opening the visit
      const sources: Record<string, "user" | "ai"> = {};
      Object.keys(profileDraft).forEach((attr) => {
        const aiVal = aiProfile ? (aiProfile as any)[attr] : undefined;
        sources[attr] = aiVal !== undefined && (profileDraft as any)[attr] === aiVal ? "ai" : "user";
      });
      const saved = await snapshotProfile(retryFetch, pendingObject._id, {
        profile: profileDraft,
        profileSource: sources,
        trigger: "capture_rating",
      });
      // Goal-directed mode: translate the stated outcome into a target state
      let targetState: MmProfile | undefined;
      if (statedOutcome.trim()) {
        try {
          const ts = await aiTargetState(retryFetch, {
            statedOutcome: statedOutcome.trim(),
            fiObject: saved.object._id,
            language,
            register,
          });
          targetState = ts.targetState;
        } catch {
          /* goal mode is optional */
        }
      }
      await openSession(saved.object, {
        statedOutcome: statedOutcome.trim() || undefined,
        targetState,
        pathId: chosenPath?.id,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  // ICDF §2 — the reason the extended brain was opened. Asked once per
  // list-initiated open (skippable); continuation opens inherit it.
  const lastReasonRef = useRef<MmVisitReason | undefined>(undefined);
  const [reasonAsk, setReasonAsk] = useState<{
    object: MmObject;
    goal?: { statedOutcome?: string; targetState?: MmProfile; pathId?: string };
  } | null>(null);

  const requestOpen = (
    object: MmObject,
    goal?: { statedOutcome?: string; targetState?: MmProfile; pathId?: string }
  ) => setReasonAsk({ object, goal });

  const openSession = async (
    object: MmObject,
    goal?: { statedOutcome?: string; targetState?: MmProfile; pathId?: string }
  ) => {
    setBusy(true);
    try {
      const res = await openVisit(retryFetch, {
        fiObject: object._id,
        register,
        reason: lastReasonRef.current,
        ...(goal || {}),
      });
      setDelta(null);
      setEmittedNotes([]);
      setRerate(null);
      setPickingPath(false);
      setSavingTemplate(false);
      setBuildingPath(false);
      setView({
        name: "session",
        object,
        visit: res.visit,
        menu: res.menu,
        warnings: res.warnings,
        reasonStats: res.reasonStats,
        reasonKind: lastReasonRef.current?.kind,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  // ---- session flow -----------------------------------------------------------

  const handleApply = async (args: { operatorId: string; input?: any; output?: any }) => {
    if (view.name !== "session") return;
    setBusy(true);
    try {
      const res = await applyOperator(retryFetch, view.visit._id, {
        ...args,
        proposedBy: "user",
      });
      if ((res as any).fault) {
        // guard rejection — refresh menu from response if present
        if ((res as any).menu) {
          setView({ ...view, menu: (res as any).menu });
        }
        return;
      }
      const notes: string[] = [];
      // Surface the operator's own output first — the user should see what
      // their move produced, not only its side-effects
      const out = args.output || {};
      const outSummary =
        out.chosenName ||
        out.chosenFrame ||
        out.probe ||
        out.firstStep ||
        out.synthesis ||
        out.problem ||
        (typeof out.response === "string" ? out.response : "");
      if (outSummary) {
        notes.push(
          `${tm(`op_${args.operatorId}_title`, args.operatorId)}: ${String(outSummary).slice(0, 140)}`
        );
      }
      (res.emitted || []).forEach((a) => {
        notes.push(
          a.artifactType === "insight"
            ? tm("emitted_insight", "An insight was saved to your workspace.")
            : tm("emitted_takeaway", "A commitment was added to your Action Items.")
        );
      });
      if (res.children && res.children.length) {
        notes.push(
          `${res.children.length} ${tm("emitted_children", "new thought(s) were created from the parts — find them in the list.")}`
        );
      }
      setEmittedNotes(notes);
      setDelta(null);
      // The operate → re-profile loop: ask how it sits now, before the next move
      setRerate({ before: res.delta.before, draft: { ...res.object.profile } });
      // Advance planned route locally
      const plannedRoute = view.visit.plannedRoute.map((s) =>
        s.operatorId === args.operatorId && s.status === "pending"
          ? { ...s, status: "done" as const }
          : s
      );
      setView({
        ...view,
        object: res.object,
        visit: { ...view.visit, plannedRoute },
        menu: res.menu,
        warnings: res.warnings,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const saveRerate = async () => {
    if (view.name !== "session" || !rerate) return;
    setBusy(true);
    try {
      const saved = await snapshotProfile(retryFetch, view.object._id, {
        profile: rerate.draft,
        trigger: "post_operator",
        fiVisit: view.visit._id,
      });
      setDelta({ before: rerate.before, after: saved.object.profile });
      setRerate(null);
      // Drop profile-based guard warnings the re-rating just invalidated
      // (the server recomputes the authoritative menu on the next apply)
      const p = saved.object.profile;
      const warnings = view.warnings.filter((w) => {
        if (w === "R2_examine_before_reframe") return (p.certainty ?? 3) >= 4;
        if (w === "R1_distance_first")
          return (p.charge ?? 0) <= -4 && (p.identification ?? 3) >= 4;
        return true;
      });
      setView({ ...view, object: saved.object, warnings });
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const resetGoal = async () => {
    if (view.name !== "session" || !goalDraft.trim()) return;
    setBusy(true);
    try {
      const ts = await aiTargetState(retryFetch, {
        statedOutcome: goalDraft.trim(),
        fiObject: view.object._id,
        language,
        register,
      });
      // A goal change is a new change attempt: close this visit, open a fresh
      // one carrying the new outcome + target — the server persists it on the
      // object and returns the new plan.
      try {
        await closeVisit(retryFetch, view.visit._id, "completed");
      } catch {
        /* best-effort */
      }
      setEditingGoal(false);
      setGoalDraft("");
      await openSession(view.object, {
        statedOutcome: goalDraft.trim(),
        targetState: ts.targetState,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const handleRetire = async (retiredNote: string) => {
    if (view.name !== "session") return;
    setBusy(true);
    try {
      await changeStatus(retryFetch, view.object._id, { status: "retired", retiredNote });
      await endSession("completed");
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const handlePark = async (parkedCondition: string) => {
    if (view.name !== "session") return;
    setBusy(true);
    try {
      // Date-looking conditions become parkedUntil; anything else is an event condition
      const asDate = new Date(parkedCondition);
      const isDate = !isNaN(asDate.getTime()) && /\d/.test(parkedCondition);
      await changeStatus(retryFetch, view.object._id, {
        status: "parked",
        ...(isDate ? { parkedUntil: asDate.toISOString() } : { parkedCondition }),
      });
      await endSession("completed");
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const endSession = async (status: "completed" | "abandoned") => {
    if (view.name !== "session") return;
    try {
      await closeVisit(retryFetch, view.visit._id, status);
    } catch {
      /* closing is best-effort */
    }
    // Lifted object: propose updating the source entity (user-confirmed always)
    if (status === "completed" && view.object.liftedFrom?.entityType === "user_takeaway") {
      try {
        const preview = await writebackPreview(retryFetch, view.object._id);
        if (preview.hasChange) {
          setWriteback({
            objectId: view.object._id,
            current: preview.current,
            proposed: preview.proposed,
          });
          setView({ name: "list" });
          refresh();
          return;
        }
      } catch {
        /* preview is optional */
      }
    }
    setView({ name: "list" });
    refresh();
  };

  const resolveWriteback = async (decision: "accepted" | "declined") => {
    if (!writeback) return;
    try {
      await writebackConfirm(retryFetch, writeback.objectId, {
        decision,
        proposed: decision === "accepted" ? writeback.proposed : undefined,
      });
    } catch (e) {
      console.error(e);
    }
    setWriteback(null);
  };

  const showBiography = async (object: MmObject) => {
    setBusy(true);
    try {
      const res = await getBiography(retryFetch, object._id);
      setBioEvents(res.events);
      setBioSeries(res.series);
      setBioReturn(
        view.name === "session" && view.object._id === object._id ? view : null
      );
      setView({ name: "biography", object: res.object });
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  /** Leave the biography: back into the stashed session, or a fresh one. */
  const resumeFromBiography = async (object: MmObject) => {
    if (bioReturn && bioReturn.name === "session") {
      const stashed = bioReturn;
      setBioReturn(null);
      setView(stashed);
      return;
    }
    requestOpen(object);
  };

  // ---- renders ---------------------------------------------------------------

  const renderRegisterSwitch = () => (
    <div className="flex items-center gap-1 text-xs">
      {(["plain", "guided", "expert"] as MmRegister[]).map((r) => (
        <button
          key={r}
          onClick={() => changeRegister(r)}
          className={cn(
            "rounded px-2 py-0.5",
            register === r ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:text-foreground"
          )}
        >
          {tm(`register_${r}`, r)}
        </button>
      ))}
    </div>
  );

  if (view.name === "capture") {
    return (
      <div className="max-w-xl mx-auto">
        <button
          onClick={() => setView({ name: "list" })}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("back", "Back")}
        </button>

        {distressPaused ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-900 space-y-2">
            <p className="font-medium">{tm("distress_title", "Let's pause here.")}</p>
            <p>
              {tm(
                "distress_body",
                "What you wrote sounds like a heavy moment. This tool isn't the right support for acute distress — please reach out to someone you trust or a local support line. Your note was saved privately and nothing else happens with it."
              )}
            </p>
            <Button variant="outline" onClick={() => setView({ name: "list" })}>
              {t("back", "Back")}
            </Button>
          </div>
        ) : !pendingObject ? (
          <div className="space-y-5">
            <div className="text-center pt-2">
              <span className="inline-flex items-center justify-center h-11 w-11 rounded-full bg-primary/[0.07] mb-3">
                <Brain className="w-5 h-5 text-primary/70" />
              </span>
              <h3 className="text-lg font-semibold tracking-tight">
                {tm("capture_title", "What's the thought that's bothering you?")}
              </h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto leading-relaxed">
                {tm("capture_help", "Write it exactly as it plays in your head — verbatim, unpolished.")}
              </p>
            </div>
            <textarea
              value={statement}
              onChange={(e) => setStatement(e.target.value)}
              rows={4}
              autoFocus
              placeholder={tm("capture_placeholder", "e.g. I'm too old to switch fields")}
              className={cn(
                "w-full rounded-2xl border border-border bg-white px-4 py-3.5 text-base leading-relaxed shadow-sm",
                "transition-shadow duration-150 placeholder:text-muted-foreground/60",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:border-primary/40"
              )}
            />
            <div>
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
                {tm("capture_type_label", "It feels like a…")}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {OBJECT_TYPES.map((type) => {
                  const v = typeVisual(type);
                  const Icon = v.icon;
                  const selected = objectType === type;
                  return (
                    <button
                      key={type}
                      onClick={() => setObjectType(type)}
                      className={cn(
                        "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs min-h-[32px] cursor-pointer",
                        "transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                        selected
                          ? "border-primary/50 bg-primary/[0.08] text-primary font-medium shadow-sm"
                          : "border-border/80 bg-white text-muted-foreground hover:border-primary/25 hover:text-foreground"
                      )}
                    >
                      <Icon className={cn("w-3.5 h-3.5", selected ? "text-primary" : v.fg)} />
                      {tm(`type_${type}`, type.replace("_", " "))}
                    </button>
                  );
                })}
              </div>
            </div>
            <Button
              size="lg"
              className="w-full sm:w-auto"
              onClick={captureAndInfer}
              disabled={busy || statement.trim().length < 3}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : null}
              {tm("capture_next", "Capture it")}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-2xl bg-gradient-to-br from-primary/[0.05] via-muted/40 to-transparent border border-border/50 px-5 py-4">
              <p className="text-base leading-relaxed text-foreground/90 italic">
                “{pendingObject.statement}”
              </p>
            </div>
            <div className="rounded-2xl border border-border/70 bg-white p-4 sm:p-5">
              <h3 className="text-sm font-semibold mb-4">
                {tm("rate_title", "How does it sit with you right now?")}
              </h3>
              <MmProfilePanel
                profile={profileDraft}
                profileSource={pendingObject.profileSource}
                confidence={aiProfile ? aiConfidence : undefined}
                register={register}
                objectType={pendingObject.objectType}
                onChange={(attr, value) => setProfileDraft({ ...profileDraft, [attr]: value })}
              />
            </div>
            <div className="rounded-2xl border border-border/70 bg-white p-4 sm:p-5 space-y-4">
              <div>
                <label className="text-sm font-medium">
                  {tm("goal_label", "What would you like to be different? (optional)")}
                </label>
                <input
                  value={statedOutcome}
                  onChange={(e) => setStatedOutcome(e.target.value)}
                  placeholder={tm("goal_placeholder", "e.g. stop dreading it and decide whether to do it")}
                  className="mt-1.5 w-full rounded-lg border border-border px-3 py-2 text-sm bg-background transition-shadow duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                />
              </div>
              <div>
              <label className="text-sm font-medium">
                {tm("path_pick_label", "…or follow a proven path (optional)")}
              </label>
              {chosenPath ? (
                <div className="mt-1 flex items-center gap-2 text-sm">
                  <span className="rounded-full bg-primary/10 text-primary px-2.5 py-1 text-xs font-medium">
                    {chosenPath.kind === "template"
                      ? chosenPath.name
                      : tm(`script_${chosenPath.id}_name`, chosenPath.id)}
                  </span>
                  <button
                    onClick={() => setChosenPath(null)}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    {t("cancel", "Cancel")}
                  </button>
                </div>
              ) : buildingPath ? (
                <div className="mt-1.5">
                  <MmPathEditor
                    register={register}
                    busy={busy}
                    onCancel={() => setBuildingPath(false)}
                    onSave={async (name, steps) => {
                      setBusy(true);
                      try {
                        await createTemplate(retryFetch, {
                          name,
                          operatorIds: steps,
                          objectTypes: objectType !== "other" ? [objectType] : [],
                        });
                        setBuildingPath(false);
                        setPathsVersion((v) => v + 1);
                      } catch (e) {
                        console.error(e);
                      } finally {
                        setBusy(false);
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="mt-1">
                  <MmPathPicker
                    key={pathsVersion}
                    objectType={objectType}
                    register={register}
                    onPick={(p) => setChosenPath(p)}
                    onCreateNew={() => setBuildingPath(true)}
                    compact
                  />
                </div>
              )}
            </div>
            </div>
            <Button size="lg" className="w-full sm:w-auto" onClick={beginVisit} disabled={busy}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : null}
              {tm("start_working", "Start working on it")}
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (view.name === "session") {
    return (
      <div className="max-w-xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => endSession("completed")}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground cursor-pointer transition-colors duration-150"
          >
            <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {tm("end_session", "Done for now")}
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => showBiography(view.object)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground cursor-pointer transition-colors duration-150 rounded-lg px-2 py-1 hover:bg-muted"
              title={tm("history", "History")}
            >
              <History className="w-3.5 h-3.5" /> {tm("history", "History")}
            </button>
            {renderRegisterSwitch()}
          </div>
        </div>

        <div className="rounded-2xl bg-gradient-to-br from-primary/[0.05] via-muted/40 to-transparent border border-border/50 px-5 py-4 mb-4">
          {view.object.label && (
            <p className="text-xs font-semibold uppercase tracking-wide text-primary/70 mb-1">
              {view.object.label}
            </p>
          )}
          <p className="text-base leading-relaxed text-foreground/90 italic">
            “{view.object.workingStatement || view.object.statement}”
          </p>
          {view.object.workingStatement &&
            view.object.workingStatement !== view.object.statement && (
              <details className="mt-2">
                <summary className="text-[11px] text-muted-foreground cursor-pointer hover:text-foreground transition-colors duration-150">
                  {tm("originally", "originally")}
                </summary>
                <p className="text-xs text-muted-foreground/80 italic mt-1">
                  “{view.object.statement}”
                </p>
              </details>
            )}
        </div>

        {/* ICDF §2 — recurring friction is a signal, not just a statistic:
            examining hasn't settled this one; suggest building something. */}
        {view.reasonKind === "friction" &&
          (view.reasonStats?.frictionVisits ?? 0) >= 3 && (
            <div className="flex items-start gap-2 rounded-xl border border-indigo-200 bg-indigo-50/70 px-4 py-3 mb-4 text-xs text-indigo-900">
              <Hammer className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-indigo-500" />
              <span>
                {tm(
                  "friction_recurring",
                  "Friction has brought you here {{count}} times now. When a thought keeps coming back, another look may not be enough — it might want a designed response. The design moves can build one."
                ).replace("{{count}}", String(view.reasonStats?.frictionVisits))}
              </span>
            </div>
          )}

        {editingGoal ? (
          <div className="rounded-lg border border-border p-3 mb-3 space-y-2">
            <label className="text-sm font-medium">
              {tm("goal_label", "What would you like to be different? (optional)")}
            </label>
            <input
              value={goalDraft}
              onChange={(e) => setGoalDraft(e.target.value)}
              placeholder={tm("goal_placeholder", "e.g. stop dreading it and decide whether to do it")}
              className="w-full rounded-md border border-border px-3 py-2 text-sm bg-background"
              autoFocus
            />
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={resetGoal} disabled={busy || !goalDraft.trim()}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : null}
                {tm("get_new_plan", "Get a new plan")}
              </Button>
              <button
                onClick={() => setEditingGoal(false)}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                {t("cancel", "Cancel")}
              </button>
            </div>
          </div>
        ) : view.visit.plannedRoute.length > 0 ? (
          <div className="rounded-xl border border-border/60 bg-white/60 px-4 py-3 mb-4">
            {view.visit.statedOutcome && (
              <p className="text-xs text-muted-foreground mb-2.5">
                {tm("plan_toward", "Toward:")}{" "}
                <span className="text-foreground/80 font-medium">“{view.visit.statedOutcome}”</span>
              </p>
            )}
            <div className="flex flex-wrap items-center gap-y-2">
              {view.visit.plannedRoute.map((step, i) => {
                const done = step.status === "done";
                const isCurrent =
                  !done && view.visit.plannedRoute.findIndex((s) => s.status !== "done") === i;
                const clickable = !done && view.menu.allowed.includes(step.operatorId);
                const Chip = clickable ? "button" : "span";
                return (
                  <span key={`${step.operatorId}-${i}`} className="flex items-center">
                    {i > 0 && (
                      <span
                        className={cn(
                          "h-px w-3 mx-0.5",
                          done ? "bg-emerald-300" : "bg-border"
                        )}
                      />
                    )}
                    <Chip
                      {...(clickable
                        ? {
                            onClick: () => setRequestedOperator(step.operatorId),
                            title: tm("stepper_start_hint", "Start this step"),
                          }
                        : {})}
                      className={cn(
                        "flex items-center gap-1.5 text-[11px] rounded-full ps-1 pe-2.5 py-0.5 border transition-all duration-150",
                        done
                          ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                          : isCurrent
                          ? "bg-primary/[0.07] border-primary/40 text-primary font-medium"
                          : "bg-muted/40 border-border/70 text-muted-foreground",
                        clickable &&
                          "cursor-pointer hover:border-primary/50 hover:shadow-sm hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                      )}
                    >
                      <span
                        className={cn(
                          "flex items-center justify-center h-4 w-4 rounded-full text-[9px] font-semibold tabular-nums",
                          done
                            ? "bg-emerald-500 text-white"
                            : isCurrent
                            ? "bg-primary text-primary-foreground"
                            : "bg-border/80 text-muted-foreground"
                        )}
                      >
                        {done ? <CheckCircle2 className="w-3 h-3" /> : i + 1}
                      </span>
                      {tm(`op_${step.operatorId}_title`, step.operatorId)}
                    </Chip>
                  </span>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-2.5 pt-2 border-t border-border/40">
            <button
              onClick={() => {
                setGoalDraft(view.visit.statedOutcome || "");
                setEditingGoal(true);
              }}
              className="text-[11px] text-primary/80 hover:text-primary cursor-pointer transition-colors duration-150"
            >
              {tm("change_goal", "change goal")}
            </button>
            <button
              onClick={() => setSavingTemplate(true)}
              className="text-[11px] text-muted-foreground hover:text-foreground cursor-pointer transition-colors duration-150"
            >
              {tm("save_as_template", "save as path")}
            </button>
            </div>
            {savingTemplate && (
              <div className="mt-3">
                <MmPathEditor
                  register={register}
                  initialSteps={view.visit.plannedRoute.map((s) => s.operatorId)}
                  busy={busy}
                  onCancel={() => setSavingTemplate(false)}
                  onSave={async (name, steps) => {
                    setBusy(true);
                    try {
                      await createTemplate(retryFetch, {
                        name,
                        operatorIds: steps,
                        objectTypes: [view.object.objectType],
                      });
                      setSavingTemplate(false);
                      setPathsVersion((v) => v + 1);
                    } catch (e) {
                      console.error(e);
                    } finally {
                      setBusy(false);
                    }
                  }}
                />
              </div>
            )}
          </div>
        ) : pickingPath ? (
          <div className="rounded-lg border border-border p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">
                {tm("path_pick_label", "…or follow a proven path (optional)")}
              </span>
              <button
                onClick={() => setPickingPath(false)}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                {t("cancel", "Cancel")}
              </button>
            </div>
            {buildingPath ? (
              <MmPathEditor
                register={register}
                busy={busy}
                onCancel={() => setBuildingPath(false)}
                onSave={async (name, steps) => {
                  setBusy(true);
                  try {
                    await createTemplate(retryFetch, {
                      name,
                      operatorIds: steps,
                      objectTypes: [view.object.objectType],
                    });
                    setBuildingPath(false);
                    setPathsVersion((v) => v + 1);
                  } catch (e) {
                    console.error(e);
                  } finally {
                    setBusy(false);
                  }
                }}
              />
            ) : (
              <MmPathPicker
                key={pathsVersion}
                objectType={view.object.objectType}
                register={register}
                compact
                onCreateNew={() => setBuildingPath(true)}
                onPick={async (p) => {
                  try {
                    await closeVisit(retryFetch, view.visit._id, "completed");
                  } catch {
                    /* best-effort */
                  }
                  await openSession(view.object, { pathId: p.id });
                }}
              />
            )}
          </div>
        ) : (
          <div className="mb-3 flex items-center gap-3">
            <button
              onClick={() => {
                setGoalDraft("");
                setEditingGoal(true);
              }}
              className="text-xs text-primary/70 hover:text-primary underline underline-offset-2"
            >
              {tm("set_goal", "Set a goal for this thought")}
            </button>
            <button
              onClick={() => setPickingPath(true)}
              className="text-xs text-primary/70 hover:text-primary underline underline-offset-2"
            >
              {tm("pick_path", "Follow a path")}
            </button>
          </div>
        )}

        {emittedNotes.length > 0 && (
          <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 mb-3 text-sm space-y-1">
            {emittedNotes.map((note, i) => (
              <p key={i} className="flex items-center gap-1.5 text-xs">
                <CheckCircle2 className="w-3.5 h-3.5 text-primary flex-shrink-0" /> {note}
              </p>
            ))}
          </div>
        )}

        {delta && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 mb-3 text-sm text-emerald-900">
            <p className="font-medium mb-1">{tm("delta_title", "Something moved:")}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
              {(["charge", "resolution", "certainty", "identification"] as const).map((attr) => {
                const b = delta.before[attr];
                const a = delta.after[attr];
                if (b === undefined || a === undefined || b === a) return null;
                return (
                  <span key={attr} className="tabular-nums">
                    {tm(`attr_${attr}_short`, attr)}: {b} → <b>{a}</b>
                  </span>
                );
              })}
              {(["charge", "resolution", "certainty", "identification"] as const).every(
                (attr) => delta.before[attr] === delta.after[attr]
              ) && <span>{tm("delta_none", "Ratings held steady — that's information too.")}</span>}
            </div>
          </div>
        )}

        {rerate ? (
          <div className="rounded-xl border border-border p-4">
            <h4 className="text-sm font-semibold mb-3">
              {tm("rerate_title", "How does it sit with you now?")}
            </h4>
            <MmProfilePanel
              profile={rerate.draft}
              register={register}
              objectType={view.object.objectType}
              onChange={(attr, value) => setRerate({ ...rerate, draft: { ...rerate.draft, [attr]: value } })}
            />
            <Button className="mt-3" onClick={saveRerate} disabled={busy}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : null}
              {tm("rerate_save", "Save & continue")}
            </Button>
          </div>
        ) : (
          <MmOperatorFlow
            object={view.object}
            menu={view.menu}
            warnings={view.warnings}
            register={register}
            plannedRoute={view.visit.plannedRoute}
            applying={busy}
            requestOperator={requestedOperator}
            onRequestConsumed={() => setRequestedOperator(null)}
            newOperatorIds={newOperatorIds}
            onApply={handleApply}
            onPark={handlePark}
            onRetire={handleRetire}
          />
        )}
      </div>
    );
  }

  if (view.name === "biography") {
    return (
      <div className="max-w-xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => {
              if (bioReturn) {
                resumeFromBiography(view.object);
              } else {
                setView({ name: "list" });
              }
            }}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground cursor-pointer transition-colors duration-150"
          >
            <ArrowLeft className="w-4 h-4 rtl:rotate-180" />{" "}
            {bioReturn ? tm("back_to_session", "Back to session") : t("back", "Back")}
          </button>
          <Button size="sm" onClick={() => resumeFromBiography(view.object)} disabled={busy}>
            {tm("continue_working", "Continue working on it")}
          </Button>
        </div>
        <div className="rounded-2xl bg-gradient-to-br from-primary/[0.05] via-muted/40 to-transparent border border-border/50 px-5 py-4 mb-4">
          {view.object.label && (
            <p className="text-xs font-semibold uppercase tracking-wide text-primary/70 mb-1">
              {view.object.label}
            </p>
          )}
          <p className="text-sm leading-relaxed italic text-foreground/90">
            “{view.object.workingStatement || view.object.statement}”
          </p>
        </div>
        <MmBiography events={bioEvents} series={bioSeries} />
        <Button className="mt-4" onClick={() => resumeFromBiography(view.object)} disabled={busy}>
          {tm("continue_working", "Continue working on it")}
        </Button>
      </div>
    );
  }

  // list view
  return (
    <div className="max-w-2xl mx-auto">
      {/* ICDF: why are you opening the extended brain? (skippable) */}
      {reasonAsk && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl">
            <p className="text-sm font-semibold mb-1">
              {tm("reason_title", "What brings you here?")}
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              {tm("reason_subtitle", "Knowing why helps pick the right moves.")}
            </p>
            <div className="space-y-2">
              {(
                [
                  { key: "reason_friction_external", fb: "Something happened", reason: { kind: "friction", frictionType: "external" } },
                  { key: "reason_friction_internal", fb: "Something rose up in me", reason: { kind: "friction", frictionType: "internal" } },
                  { key: "reason_plan", fb: "I'm working a plan", reason: { kind: "plan" } },
                ] as Array<{ key: string; fb: string; reason: MmVisitReason }>
              ).map(({ key, fb, reason }) => (
                <button
                  key={key}
                  disabled={busy}
                  onClick={() => {
                    lastReasonRef.current = reason;
                    const ask = reasonAsk;
                    setReasonAsk(null);
                    openSession(ask.object, ask.goal);
                  }}
                  className="w-full rounded-xl border border-border px-4 py-2.5 text-sm text-start hover:border-primary/50 hover:bg-primary/5 transition-colors"
                >
                  {tm(key, fb)}
                </button>
              ))}
            </div>
            <button
              disabled={busy}
              onClick={() => {
                lastReasonRef.current = undefined;
                const ask = reasonAsk;
                setReasonAsk(null);
                openSession(ask.object, ask.goal);
              }}
              className="mt-3 w-full text-center text-xs text-muted-foreground hover:text-foreground"
            >
              {tm("reason_skip", "Just open it")}
            </button>
          </div>
        </div>
      )}
      {writeback && (
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 mb-4">
          <p className="text-sm font-medium mb-2">
            {tm("writeback_title", "Your thinking moved — update the source item?")}
          </p>
          <div className="text-xs space-y-1 mb-3">
            <p className="text-muted-foreground line-through">{writeback.current}</p>
            <p className="text-foreground">{writeback.proposed}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => resolveWriteback("accepted")}>
              {tm("writeback_accept", "Update it")}
            </Button>
            <Button size="sm" variant="outline" onClick={() => resolveWriteback("declined")}>
              {tm("writeback_decline", "Keep the original")}
            </Button>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">
            {tm("studio_title", "Mental Models")}
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {tm("studio_subtitle", "Beliefs, fears and goals you're deliberately reworking.")}
          </p>
        </div>
        {renderRegisterSwitch()}
      </div>

      <Button onClick={startCapture} className="mb-4">
        <Plus className="w-4 h-4 me-1.5" />
        {tm("new_object", "Work on a thought")}
      </Button>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
          <Loader2 className="w-4 h-4 animate-spin" /> {t("loading", "Loading…")}
        </div>
      ) : objects.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-gradient-to-b from-muted/30 to-transparent px-8 py-12 text-center text-sm text-muted-foreground">
          <span className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-primary/[0.07] mb-3">
            <Brain className="w-6 h-6 text-primary/60" />
          </span>
          <p className="max-w-xs mx-auto leading-relaxed">
            {tm(
              "empty_state",
              "Nothing here yet. Bring the sentence that keeps looping in your head."
            )}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {objects.map((o) => (
            <div
              key={o._id}
              className="group flex items-center gap-3.5 rounded-2xl border border-border/70 bg-white p-4 transition-all duration-150 hover:shadow-sm hover:border-primary/25"
            >
              {(() => {
                const v = typeVisual(o.objectType);
                const Icon = v.icon;
                return (
                  <span
                    className={cn(
                      "flex items-center justify-center h-10 w-10 rounded-xl flex-shrink-0",
                      v.bg
                    )}
                  >
                    <Icon className={cn("w-5 h-5", v.fg)} />
                  </span>
                );
              })()}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium leading-snug line-clamp-2">
                  {o.label || o.workingStatement || o.statement}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {tm(`type_${o.objectType}`, o.objectType.replace("_", " "))}
                  <span className="mx-1.5 text-border">·</span>
                  {o.visitCount} {tm("visits", "visits")}
                  {o.status === "parked" && (
                    <span className="inline-flex items-center gap-0.5 ms-2 text-purple-600">
                      <PauseCircle className="w-3 h-3" /> {tm("parked", "parked")}
                    </span>
                  )}
                </p>
                {o.activeGoal?.statedOutcome && (
                  <p className="text-xs text-primary/80 mt-1 truncate">
                    {tm("plan_toward", "Toward:")} {o.activeGoal.statedOutcome}
                  </p>
                )}
              </div>
              {typeof o.profile.charge === "number" && (
                <span
                  className={cn(
                    "text-xs font-semibold tabular-nums rounded-full px-2 py-0.5 flex-shrink-0",
                    o.profile.charge <= -3
                      ? "bg-rose-50 text-rose-700"
                      : o.profile.charge >= 2
                      ? "bg-emerald-50 text-emerald-700"
                      : "bg-muted text-muted-foreground"
                  )}
                >
                  {o.profile.charge > 0 ? `+${o.profile.charge}` : o.profile.charge}
                </span>
              )}
              <button
                onClick={() => showBiography(o)}
                className="flex items-center justify-center h-9 w-9 rounded-lg text-muted-foreground cursor-pointer transition-colors duration-150 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                title={tm("history", "History")}
                aria-label={tm("history", "History")}
              >
                <History className="w-4 h-4" />
              </button>
              <Button size="sm" variant="outline" onClick={() => requestOpen(o)} disabled={busy}>
                {tm("open", "Open")}
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* ICDF §4 — your operators, as a growing skill constellation */}
      <MmMasteryPanel mastery={mastery} reasonMix={reasonMix} register={register} />

      {/* ICDF §5.2 — Reality-Shaping: what the period's experiences keep asking */}
      <MmRealityShaping
        register={register}
        onDesign={(text) => {
          startCapture();
          setStatement(text);
          setObjectType("goal");
        }}
      />
    </div>
  );
};

export default MmStudio;
